





















import { Component, Prop, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ImageObjectTypes } from "@/models/images";
import { ConfirmModalModes } from "@/data/enums";

// Types
import { IState } from "@/store";
import { IProduct, IProductCategory } from "@/models/products";
import { IImage } from "@/store/types";

@Component({
  computed: {
    ...mapState({
      imagesScope(state: IState, getters) {
        return getters[`data/${this.dataModule}/imagesScope`](
          this.objectId,
          this.imageType
        );
      },
      images(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.dataModule,
          scope: this.imagesScope
        });
      }
    })
  }
})
export default class ProductGallery extends Mixins(RequestPromises) {
  // Getters
  public imagesScope!: string;
  public images!: any;

  // Props
  @Prop({ default: null })
  public productId!: IProduct["id"];

  // Props
  @Prop({ default: null })
  public categoryId!: IProductCategory["id"];

  @Prop({ default: 8 })
  public limit!: number;

  @Prop({ default: false })
  public isDisabled!: boolean;

  // Variables
  public dataModule: string = DataModules.IMAGES;
  public isLoading = true;
  public isReloading = false;
  public requestError = false;
  public showLoading = true;

  public created() {
    this.loadData();
  }

  public async loadData() {
    try {
      await this.getProductImages();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }

    this.isLoading = false;
    this.isReloading = false;

    setTimeout(() => {
      this.showLoading = false;
    }, 500);
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getProductImages(ignoreStored = false) {
    return this.$store.dispatch(`data/${this.dataModule}/list`, {
      typeId: this.objectId,
      imageType: this.imageType,
      scope: this.imagesScope,
      vm: this,
      ignoreStored,
      params: {
        limit: this.limit,
        order: "order"
      }
    });
  }

  public get defaultImage() {
    return this.$_.find(this.images, image => image.default);
  }

  public get objectId() {
    return this.productId ? this.productId : this.categoryId;
  }

  public refreshImages() {
    return this.getProductImages(true);
  }

  public reorder(imageIds) {
    return this.$store.dispatch(`data/${this.dataModule}/reorder`, {
      typeId: this.objectId,
      imageType: this.imageType,
      vm: this,
      data: {
        image_ids: imageIds
      }
    });
  }

  public get imageType() {
    return this.productId
      ? ImageObjectTypes.PRODUCT
      : ImageObjectTypes.PRODUCT_CATEGORY;
  }

  public async newImages(images, imageId) {
    try {
      const selectedImage = this.$_.find(this.images, ["id", imageId]);

      await Promise.all(
        this.$_.map(images, (image, index) => {
          const data = new FormData();
          data.append("image", image);

          return this.uploadImage(
            data,
            !index &&
              (!this.images.length ||
                this.$_.get(selectedImage, "default", false))
          );
        })
      );

      const selectedImageHash = this.$_.get(selectedImage, "hash");

      if (selectedImageHash) {
        await this.deleteImage(selectedImageHash);
      }

      await this.getProductImages(true);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public async openDeleteConfirmation(imageId: IImage["id"]) {
    if (!this.canDeleteImage) {
      return;
    }

    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          mode: ConfirmModalModes.BUTTON,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_confirmation", {
            object: (this.$t("_.image") as string).toLowerCase()
          })
        },
        events: {
          confirmed: async () => {
            await this.deleteConfirmedImage(imageId);
            confirm.close();
          }
        }
      }
    });
  }

  public async deleteConfirmedImage(imageId: IImage["id"]) {
    try {
      const delImage = this.$_.find(this.images, ["id", imageId]);
      await this.deleteImage(this.$_.get(delImage, "hash"));

      if (this.$_.get(delImage, "default") && this.images.length > 1) {
        await this.setDefaultImage(
          this.$_.get(
            this.$_.filter(this.images, image => image.id !== imageId),
            "[0].hash"
          )
        );
      }

      await this.getProductImages(true);

      this.$buefy.toast.open({
        message: this.$t("_.object_successfully_removed", {
          object: this.$t("_.image")
        }) as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public async setDefault(imageId: IImage["id"]) {
    try {
      await this.setDefaultImage(
        this.$_.get(this.$_.find(this.images, ["id", imageId]), "hash")
      );
      await this.getProductImages(true);

      this.$buefy.toast.open({
        message: this.$t("_.default_image_set") as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public deleteImage(hash: string) {
    return this.$store.dispatch(`data/${this.dataModule}/deleteImage`, {
      typeId: this.objectId,
      imageType: this.imageType,
      hash,
      vm: this
    });
  }

  public setDefaultImage(hash) {
    return this.$store.dispatch(`data/${this.dataModule}/setDefault`, {
      typeId: this.objectId,
      imageType: this.imageType,
      hash,
      params: {
        default: true
      },
      vm: this
    });
  }

  public uploadImage(data, isDefault = false) {
    return this.$store.dispatch(`data/${this.dataModule}/uploadImage`, {
      typeId: this.objectId,
      imageType: this.imageType,
      isDefault: isDefault,
      data,
      vm: this
    });
  }

  public get canDeleteImage() {
    return this.productId
      ? this.$userCan("delete_product_image")
      : this.$userCan("delete_product_category_image");
  }

  public get canManageProductImage() {
    return (
      this.$userCan("upload_product_default_image") &&
      this.$userCan("delete_product_image")
    );
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: this.dataModule,
      scope: this.imagesScope,
      vm: this
    });
  }
}
